import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * Parcours d'achat V2 - Québec
 * Timeline d'achat avec checklists
 */
const AchatPageV2 = () => {
  const navigate = useNavigate();
  const [expandedStep, setExpandedStep] = useState(1);

  const steps = [
    {
      id: 1,
      title: 'Validation du deal',
      icon: 'CheckCircle',
      status: 'current',
      description: 'Confirmer que les chiffres tiennent la route',
      items: [
        { label: 'Vérifier les revenus déclarés', completed: true },
        { label: 'Valider les dépenses réelles', completed: false },
        { label: 'Confirmer le financement possible', completed: false }
      ],
      risk: 'Risque si ignoré: acheter un immeuble non rentable'
    },
    {
      id: 2,
      title: 'Offre acceptée',
      icon: 'FileText',
      status: 'pending',
      description: 'Négociation et signature de la promesse d\'achat',
      items: [
        { label: 'Rédiger la promesse d\'achat', completed: false },
        { label: 'Négocier les conditions', completed: false },
        { label: 'Obtenir l\'acceptation du vendeur', completed: false }
      ],
      risk: 'Risque si ignoré: perdre la propriété ou payer trop cher'
    },
    {
      id: 3,
      title: 'Inspection',
      icon: 'Search',
      status: 'pending',
      description: 'Vérification de l\'état physique du bâtiment',
      items: [
        { label: 'Inspection pré-achat complète', completed: false },
        { label: 'Vérification de la toiture', completed: false },
        { label: 'Inspection de la plomberie/électricité', completed: false },
        { label: 'Évaluation de la fondation', completed: false }
      ],
      documents: ['Rapport d\'inspection', 'Photos des défauts'],
      risk: 'Risque si ignoré: découvrir des vices cachés après l\'achat'
    },
    {
      id: 4,
      title: 'Baux & locataires',
      icon: 'Users',
      status: 'pending',
      description: 'Analyser les baux et valider les locataires',
      items: [
        { label: 'Obtenir tous les baux signés', completed: false },
        { label: 'Vérifier les dates de renouvellement', completed: false },
        { label: 'Valider les loyers déclarés', completed: false },
        { label: 'Évaluer l\'historique de paiement', completed: false }
      ],
      documents: ['Copie des baux', 'Historique de paiement'],
      risk: 'Risque si ignoré: locataires problématiques ou loyers surévalués'
    },
    {
      id: 5,
      title: 'Légal & municipal',
      icon: 'Scale',
      status: 'pending',
      description: 'Vérifications légales et conformité',
      items: [
        { label: 'Certificat de localisation', completed: false },
        { label: 'Vérifier le zonage', completed: false },
        { label: 'Taxes municipales à jour', completed: false },
        { label: 'Permis de location valides', completed: false }
      ],
      documents: ['Certificat de localisation', 'Compte de taxes'],
      risk: 'Risque si ignoré: problèmes légaux post-achat'
    },
    {
      id: 6,
      title: 'Financement',
      icon: 'Landmark',
      status: 'pending',
      description: 'Obtenir l\'approbation finale du prêt',
      items: [
        { label: 'Pré-approbation hypothécaire', completed: false },
        { label: 'Soumettre le dossier complet', completed: false },
        { label: 'Obtenir l\'engagement ferme', completed: false },
        { label: 'Confirmer les conditions du prêt', completed: false }
      ],
      documents: ['Lettre d\'engagement', 'Conditions du prêt'],
      risk: 'Risque si ignoré: financement refusé à la dernière minute'
    },
    {
      id: 7,
      title: 'Notaire',
      icon: 'FileSignature',
      status: 'pending',
      description: 'Préparation de l\'acte de vente',
      items: [
        { label: 'Choisir un notaire', completed: false },
        { label: 'Réviser l\'acte de vente', completed: false },
        { label: 'Préparer les fonds pour la clôture', completed: false }
      ],
      documents: ['Acte de vente préliminaire', 'État des ajustements'],
      risk: 'Risque si ignoré: erreurs dans l\'acte ou fonds manquants'
    },
    {
      id: 8,
      title: 'Prise de possession',
      icon: 'Key',
      status: 'pending',
      description: 'Finalisation de l\'achat',
      items: [
        { label: 'Signature chez le notaire', completed: false },
        { label: 'Transfert des fonds', completed: false },
        { label: 'Réception des clés', completed: false },
        { label: 'Visite avec les locataires', completed: false }
      ],
      risk: 'Risque si ignoré: transition chaotique avec les locataires'
    }
  ];

  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const progressPercent = (completedSteps / steps.length) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: 'white'
    }}>
      {/* Header avec barre de parcours */}
      <header style={{
        padding: '1.5rem 2rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <button
            onClick={() => navigate('/property-analysis-wizard')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem'
            }}
          >
            <Icon name="ArrowLeft" size={18} />
            <span>Retour à l'analyse</span>
          </button>
        </div>

        {/* Flow Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          {['Analyse', 'Achat', 'Détention', 'Croissance'].map((stage, index) => (
            <React.Fragment key={stage}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '100px',
                background: index === 1 ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)' : 'transparent',
                border: index === 1 ? 'none' : '1px solid rgba(255,255,255,0.1)',
                color: index === 1 ? 'white' : index < 1 ? '#22c55e' : 'rgba(255,255,255,0.4)',
                fontSize: '0.875rem',
                fontWeight: index === 1 ? 600 : 400
              }}>
                {index < 1 && <Icon name="CheckCircle" size={14} />}
                {index === 1 && <Icon name="Circle" size={14} />}
                <span>{stage}</span>
              </div>
              {index < 3 && (
                <div style={{
                  width: '24px',
                  height: '2px',
                  background: index < 1 ? '#22c55e' : 'rgba(255,255,255,0.2)'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </header>

      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        {/* Progress Overview */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Progression de l'achat
            </h2>
            <span style={{
              background: 'rgba(59, 130, 246, 0.2)',
              color: '#60a5fa',
              padding: '0.25rem 0.75rem',
              borderRadius: '100px',
              fontSize: '0.875rem'
            }}>
              {completedSteps}/{steps.length} étapes
            </span>
          </div>
          
          <div style={{
            height: '8px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Steps Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {steps.map((step, index) => (
            <div
              key={step.id}
              style={{
                background: expandedStep === step.id 
                  ? 'rgba(59, 130, 246, 0.1)' 
                  : 'rgba(255,255,255,0.03)',
                border: `1px solid ${expandedStep === step.id 
                  ? 'rgba(59, 130, 246, 0.3)' 
                  : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
            >
              {/* Step Header */}
              <button
                onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                style={{
                  width: '100%',
                  padding: '1.25rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  color: 'white',
                  textAlign: 'left'
                }}
              >
                {/* Step number/icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: step.status === 'completed' 
                    ? '#22c55e' 
                    : step.status === 'current'
                      ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
                      : 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {step.status === 'completed' ? (
                    <Icon name="Check" size={24} color="white" />
                  ) : (
                    <span style={{ fontSize: '1.125rem', fontWeight: 600 }}>{step.id}</span>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: 600, 
                    marginBottom: '0.25rem',
                    color: step.status === 'pending' ? 'rgba(255,255,255,0.6)' : 'white'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    {step.description}
                  </p>
                </div>

                <Icon 
                  name={expandedStep === step.id ? 'ChevronUp' : 'ChevronDown'} 
                  size={20} 
                  color="rgba(255,255,255,0.5)" 
                />
              </button>

              {/* Expanded Content */}
              {expandedStep === step.id && (
                <div style={{
                  padding: '0 1.25rem 1.25rem',
                  borderTop: '1px solid rgba(255,255,255,0.1)'
                }}>
                  {/* Checklist */}
                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ 
                      fontSize: '0.75rem', 
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'rgba(255,255,255,0.5)',
                      marginBottom: '0.75rem'
                    }}>
                      Checklist
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {step.items.map((item, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '8px'
                          }}
                        >
                          <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '6px',
                            border: item.completed 
                              ? 'none' 
                              : '2px solid rgba(255,255,255,0.3)',
                            background: item.completed ? '#22c55e' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {item.completed && <Icon name="Check" size={14} color="white" />}
                          </div>
                          <span style={{
                            fontSize: '0.875rem',
                            color: item.completed ? 'rgba(255,255,255,0.5)' : 'white',
                            textDecoration: item.completed ? 'line-through' : 'none'
                          }}>
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Documents */}
                  {step.documents && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ 
                        fontSize: '0.75rem', 
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'rgba(255,255,255,0.5)',
                        marginBottom: '0.5rem'
                      }}>
                        Documents à obtenir
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {step.documents.map((doc, i) => (
                          <span
                            key={i}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Icon name="FileText" size={12} />
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk Warning */}
                  {step.risk && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(251, 191, 36, 0.1)',
                      border: '1px solid rgba(251, 191, 36, 0.2)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <Icon name="AlertTriangle" size={16} color="#fbbf24" style={{ marginTop: '2px' }} />
                      <p style={{ fontSize: '0.75rem', color: '#fbbf24' }}>
                        {step.risk}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Footer */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <Icon name="Sparkles" size={24} color="#22c55e" style={{ marginBottom: '0.5rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Tu as complété l'achat?
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Passe à l'étape de détention pour suivre ta propriété.
          </p>
          <button
            onClick={() => navigate('/v2/detention')}
            style={{
              background: '#22c55e',
              color: 'white',
              border: 'none',
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>Marquer comme acquis</span>
            <Icon name="ArrowRight" size={16} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default AchatPageV2;
